import { formatTimestamp } from '@/helper/utils';
import { WalletTransactions } from '@/types/wallet.type';
import getSymbolFromCurrency from 'currency-symbol-map';

type Props = {
  txn: WalletTransactions;
  selectedCurrency?: string;
  convertedAmount?: number;
};

export default function TransactionExportCard({
  txn,
  selectedCurrency = 'inr',
  convertedAmount,
}: Props) {
  const currencySymbol = getSymbolFromCurrency(selectedCurrency.toUpperCase()) || '₹';
  const displayAmount = convertedAmount !== undefined ? convertedAmount : Number(txn.amount || 0);
  return (
    <div
      style={{
        width: '500px',
        fontFamily: 'Arial, sans-serif',
        color: '#000000',
        background: '#ffffff',
        padding: '0',
        margin: '0',
      }}
    >
      {/* Header with Logo and Company Info */}
      <div
        style={{
          background: 'linear-gradient(135deg, #AD0C62 0%, #621C43 100%)',
          color: '#ffffff',
          padding: '20px',
          textAlign: 'center',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}
        >
          <img
            src="/images/logo.png"
            alt="MillionMakers Logo"
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'contain',
            }}
            onError={(e) => {
              // Fallback if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              (target.nextElementSibling as HTMLElement).style.display = 'flex';
            }}
          />
          <div
            style={{
              width: '50px',
              height: '50px',
              background: '#ffffff',
              borderRadius: '50%',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#AD0C62',
            }}
          >
            MM
          </div>
          <div>
            <h1 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold' }}>MillionMakers™</h1>
            <p style={{ margin: '0', fontSize: '14px', opacity: '0.9' }}>Transaction Receipt</p>
          </div>
        </div>
      </div>

      {/* Receipt Content */}
      <div
        style={{
          background: '#ffffff',
          padding: '25px',
          border: '2px solid #E0C9D8',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
        }}
      >
        {/* Receipt Header */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '2px dashed #E0C9D8',
          }}
        >
          <h2 style={{ margin: '0', fontSize: '18px', color: '#4A082D', fontWeight: 'bold' }}>
            PAYMENT RECEIPT
          </h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
            Transaction ID:{' '}
            {String(txn.transactionId ?? txn.rewardTransactionId ?? txn.id ?? '').toUpperCase()}
          </p>
        </div>

        {/* Transaction Details */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#4A082D',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Project
              </span>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{txn.projectName ?? '--'}</div>
            </div>

            <div>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#4A082D',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Survey Set
              </span>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{txn.surveySetName ?? '--'}</div>
            </div>

            <div>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#4A082D',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Payment Mode
              </span>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {txn.transactionMode ?? txn.type}
              </div>
            </div>

            <div>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#4A082D',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Date & Time
              </span>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {formatTimestamp(txn.createdAt)}
              </div>
            </div>

            <div>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#4A082D',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Transaction Status
              </span>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color:
                    txn.status === 'COMPLETED'
                      ? '#4CAF50'
                      : txn.status === 'PENDING'
                      ? '#FF9800'
                      : '#F44336',
                }}
              >
                {txn.status}
              </div>
            </div>
          </div>
        </div>

        {/* Amount Section */}
        <div
          style={{
            background: '#E8F5E8',
            border: '2px solid #4CAF50',
            borderRadius: '8px',
            padding: '15px',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Amount</div>
          <div
            style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#4CAF50',
            }}
          >
            {currencySymbol}
            {Math.abs(displayAmount).toLocaleString('en-IN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            {selectedCurrency.toUpperCase()} - Credit Transaction
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: '2px dashed #E0C9D8',
            paddingTop: '15px',
            textAlign: 'center',
            fontSize: '11px',
            color: '#666',
          }}
        >
          <p style={{ margin: '0' }}>Thank you for using MillionMakers™</p>
          <p style={{ margin: '5px 0 0 0' }}>For support, contact: support@millionmakers.com</p>
          <p style={{ margin: '5px 0 0 0' }}>Generated on: {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}
